import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/components/AppShell";
import { useCan } from "@/lib/perm";
import { ISSUE_TYPES, STATUSES, PRIORITIES, typeMeta, prioMeta, initials } from "@/lib/meta";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Sparkles, Star, Trash2, Save, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const EXAMPLES = [
  'project = WEB AND status != Done',
  'priority = highest AND type = bug',
  'assignee = currentUser()',
  'labels = critical OR priority IN (highest, high)',
  'summary ~ "login"',
];

export default function IssuesAll() {
  const { openIssue, projects, users } = useApp();
  const canSave = useCan("filter.create");
  const [issues, setIssues] = useState([]);
  const [jql, setJql] = useState("");
  const [jqlError, setJqlError] = useState(null);
  const [filters, setFilters] = useState([]);
  const [activeFilterId, setActiveFilterId] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({ name: "", shared: false });

  // Fallback simple filters if no JQL entered
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [projectId, setProjectId] = useState("all");
  const [assignee, setAssignee] = useState("all");

  const loadFilters = async () => setFilters((await api.get("/filters")).data);

  const runJql = async (query) => {
    setJqlError(null);
    if (!query || !query.trim()) {
      const r = await api.get("/issues");
      setIssues(r.data); return;
    }
    try {
      const r = await api.get("/issues/jql", { params: { jql: query } });
      setIssues(r.data);
    } catch (e) {
      setJqlError(e?.response?.data?.detail || "Invalid query");
      setIssues([]);
    }
  };

  useEffect(() => { runJql(""); loadFilters(); }, []);

  const applyFilter = (f) => {
    setActiveFilterId(f.id);
    setJql(f.jql);
    runJql(f.jql);
  };

  const clearFilter = () => {
    setActiveFilterId(null);
    setJql("");
    runJql("");
  };

  const submitJql = (e) => {
    e.preventDefault();
    setActiveFilterId(null);
    runJql(jql);
  };

  const saveFilter = async () => {
    if (!saveForm.name.trim() || !jql.trim()) return;
    await api.post("/filters", { name: saveForm.name, jql, shared: saveForm.shared });
    toast.success("Filter saved");
    setSaveOpen(false); setSaveForm({ name: "", shared: false });
    loadFilters();
  };

  const delFilter = async (id) => {
    if (!window.confirm("Delete filter?")) return;
    await api.delete(`/filters/${id}`);
    if (activeFilterId === id) clearFilter();
    loadFilters();
  };

  // Basic-filter mode (only when JQL empty)
  const filtered = jql ? issues : issues.filter((i) =>
    (q === "" || i.summary.toLowerCase().includes(q.toLowerCase()) || i.key.toLowerCase().includes(q.toLowerCase())) &&
    (type === "all" || i.issue_type === type) &&
    (status === "all" || i.status === status) &&
    (projectId === "all" || i.project_id === projectId) &&
    (assignee === "all" || (assignee === "unassigned" ? !i.assignee_id : i.assignee_id === assignee))
  );

  return (
    <div className="px-6 py-6">
      <div className="mb-5">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">All work</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Issues</h1>
      </div>

      <div className="mb-3">
        <form onSubmit={submitJql} className="flex gap-2">
          <div className="relative flex-1 max-w-2xl">
            <Sparkles className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-primary" />
            <Input value={jql} onChange={(e) => setJql(e.target.value)}
              placeholder='JQL — e.g. project = WEB AND priority = highest'
              className="pl-8 pr-8 font-mono text-sm" data-testid="jql-input" />
            {jql ? (
              <button type="button" onClick={clearFilter} className="absolute right-2 top-2 text-muted-foreground hover:text-foreground" data-testid="jql-clear">
                <X className="w-3.5 h-3.5" />
              </button>
            ) : null}
          </div>
          <Button type="submit" data-testid="jql-run">Run</Button>
          {canSave && jql.trim() ? (
            <Button type="button" variant="outline" onClick={() => setSaveOpen(true)} data-testid="jql-save" className="gap-1.5">
              <Save className="w-4 h-4" /> Save
            </Button>
          ) : null}
        </form>
        {jqlError ? <div className="text-xs text-red-500 mt-1 font-mono">{jqlError}</div> : null}
        {!jql ? (
          <div className="mt-2 text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
            <span className="uppercase tracking-widest">Try:</span>
            {EXAMPLES.map((ex) => (
              <button key={ex} type="button" onClick={() => { setJql(ex); runJql(ex); }}
                className="font-mono bg-secondary hover:bg-accent rounded px-1.5 py-0.5 transition-colors">
                {ex}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {filters.length > 0 ? (
        <div className="flex items-center gap-2 flex-wrap mb-4">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">Saved</span>
          {filters.map((f) => (
            <div key={f.id} className={cn("group flex items-center gap-1 border rounded-full text-xs transition-colors",
              activeFilterId === f.id ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40")}
              data-testid={`filter-chip-${f.id}`}>
              <button onClick={() => applyFilter(f)} className="pl-3 py-1 flex items-center gap-1.5">
                <Star className="w-3 h-3" />
                {f.name}
                {f.shared ? <span className="text-[9px] font-mono uppercase text-muted-foreground">shared</span> : null}
              </button>
              <button onClick={() => delFilter(f.id)} className="pr-2 pl-1 py-1 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity" data-testid={`del-filter-${f.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {!jql ? (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs h-9" data-testid="all-issues-search" />
          <Select value={projectId} onValueChange={setProjectId}><SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All projects</SelectItem>{projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent></Select>
          <Select value={type} onValueChange={setType}><SelectTrigger className="w-32 h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All types</SelectItem>{ISSUE_TYPES.map((t) => (<SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>))}</SelectContent></Select>
          <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => (<SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>))}</SelectContent></Select>
          <Select value={assignee} onValueChange={setAssignee}><SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Anyone</SelectItem><SelectItem value="unassigned">Unassigned</SelectItem>{users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}</SelectContent></Select>
        </div>
      ) : null}

      <div className="text-xs text-muted-foreground mb-2">{filtered.length} results</div>

      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Key</th>
              <th className="text-left px-3 py-2 font-medium">Summary</th>
              <th className="text-left px-3 py-2 font-medium">Project</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">Priority</th>
              <th className="text-left px-3 py-2 font-medium">Assignee</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 500).map((i) => {
              const T = typeMeta(i.issue_type); const P = prioMeta(i.priority);
              const a = users.find((u) => u.id === i.assignee_id);
              const p = projects.find((pp) => pp.id === i.project_id);
              return (
                <tr key={i.id} onClick={() => openIssue(i.id)} data-testid={`all-row-${i.key}`} className="border-t border-border hover:bg-accent cursor-pointer transition-colors group">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <T.icon className={cn("w-4 h-4", T.cls)} />
                      <a
                        href={`/issues/${i.key || i.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="font-mono text-xs text-muted-foreground hover:text-primary hover:underline flex items-center gap-1"
                        title="Open full page in new tab"
                      >
                        <span>{i.key}</span>
                        <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                    </div>
                  </td>
                  <td className="px-3 py-2 max-w-md truncate">{i.summary}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{p ? p.name : "—"}</td>
                  <td className="px-3 py-2"><span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border status-${i.status}`}>{i.status}</span></td>
                  <td className="px-3 py-2"><div className="flex items-center gap-1.5"><P.icon className={cn("w-4 h-4", P.cls)} /><span className="capitalize text-xs">{i.priority}</span></div></td>
                  <td className="px-3 py-2">{a ? (<div className="flex items-center gap-1.5"><Avatar className="w-5 h-5"><AvatarImage src={a.avatar_url} /><AvatarFallback className="text-[8px]">{initials(a.name)}</AvatarFallback></Avatar><span className="text-xs">{a.name}</span></div>) : (<span className="text-xs text-muted-foreground">—</span>)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No issues match.</div> : null}
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-testid="save-filter-dialog">
          <DialogHeader><DialogTitle className="font-display">Save filter</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={saveForm.name} onChange={(e) => setSaveForm({ ...saveForm, name: e.target.value })} placeholder="My critical bugs" data-testid="filter-name-input" /></div>
            <div className="border border-border rounded-md p-2 font-mono text-xs bg-secondary/40">{jql}</div>
            <div className="flex items-center gap-2">
              <Switch checked={saveForm.shared} onCheckedChange={(v) => setSaveForm({ ...saveForm, shared: v })} data-testid="filter-shared-toggle" />
              <span className="text-sm">Share with everyone in the org</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button onClick={saveFilter} data-testid="save-filter-submit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
